import { combineEpics, ofType } from 'redux-observable';
import { map, mergeMap, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';

import { rs } from 'core/api';
import { reduxSwitch } from 'core/redux/tools';
import { actions as devicesActions } from 'core/redux/api/devices';
import { internalizePage, timestamps } from 'core/redux/serializers';
import { ActionGeneratorBuilder, errorMap } from 'core/redux/actions';

const defaultPageSize = 10;

const internalize = timestamps(['createdAt', 'seenAt', 'sinceAt']).internalize;

export const actions = new ActionGeneratorBuilder('veta-entry-passes')
	.subtype('load', (load) => load.request('payload').success({ page: true }).fail())
	.subtype('update', (update) => update.request({ id: true, name: true, uri: true }).success().fail())
	.subtype('add', (add) => add.request({ uris: true }).success().fail())
	.subtype('remove', (remove) => remove.request({ id: true }).success().fail())
	.type('select', { entryPass: true })
	.type('intent', { intent: true })
	.type('clear')
	.build();

const defaultState = {
	intent: null,
	selected: null,
	data: null,
	params: { no: 1, search: undefined, size: 10, sort: undefined },
	request: { isLoading: false, error: null },
};

const entryPassesReducer = (state, action) => {
	switch (action.type) {
		case actions.load.request.type:
			return { ...state, request: { isLoading: true, error: null }, params: action.payload };
		case actions.load.success.type:
			return { ...state, intent: null, selected: null, data: action.page, request: { error: null, isLoading: false } };
		case actions.update.request.type:
		case actions.add.request.type:
		case actions.remove.request.type:
			return { ...state, request: { isLoading: true, error: null } };
		case actions.load.fail.type:
		case actions.update.fail.type:
		case actions.add.fail.type:
		case actions.remove.fail.type:
			return { ...state, request: { isLoading: false, error: action.errorMessage } };
		case actions.select.type:
			return { ...state, selected: action.entryPass };
		case actions.intent.type:
			return { ...state, intent: action.intent };
		case actions.clear.type:
			return { ...state, request: { isLoading: false, error: null } };
		default:
			return state;
	}
};

export const reducer = reduxSwitch([entryPassesReducer], defaultState);

const loadEpic = (action$) => {
	return action$.pipe(
		ofType(actions.load.request.type),
		switchMap((action) =>
			rs('veta/entry-passes/page')
				.optional('no', action.payload.no)
				.optional('sort', action.payload.sort)
				.optional('search', action.payload.search)
				.param('size', action.payload.size | defaultPageSize)
				.defer$()
				.pipe(
					map(internalizePage(action.payload.size | defaultPageSize, internalize)),
					map((page) => actions.load.success({ page })),
					errorMap(actions.load.fail),
				),
		),
	);
};

const addEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.add.request.type),
		switchMap((action) =>
			rs('veta/entry-passes/batch')
				.post({ entryPasses: action.uris.map((uri) => ({ uri })) })
				.defer$()
				.pipe(
					mergeMap(() =>
						of(
							devicesActions.load.request(),
							actions.load.request({ payload: state$.value.veta.tep.entryPasses.params }),
						),
					),
					errorMap(actions.add.fail),
				),
		),
	);
};

const updateEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.update.request.type),
		switchMap((action) =>
			rs(`veta/entry-passes/${action.id}`)
				.put({ name: action.name, uri: action.uri })
				.defer$()
				.pipe(
					mergeMap(() =>
						of(
							devicesActions.load.request(),
							actions.load.request({ payload: state$.value.veta.tep.entryPasses.params }),
						),
					),
					errorMap(actions.update.fail),
				),
		),
	);
};

const removeEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.remove.request.type),
		switchMap((action) =>
			rs(`veta/entry-passes/${action.id}`)
				.delete()
				.defer$()
				.pipe(
					map(() => actions.load.request({ payload: state$.value.veta.tep.entryPasses.params })),
					errorMap(actions.remove.fail),
				),
		),
	);
};

export const epic = combineEpics(loadEpic, addEpic, updateEpic, removeEpic);
